import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { toast } from 'sonner';
import { reportError } from '@/shared/ui/components/basic/error-notification/report-error';
import { backendPropertiesCrud } from '@services/housien';
import { uploadPropertyFileImages } from '@services/images-upload';
import { getCepData } from '@services/get-cep-data';
import { createPropertyFormSchema, propertyFormSchema } from './schema';
import { usePropertySettings } from '../../hooks/usePropertySettings';
import type { PropertyFormValues } from './schema';
import { buildDefaultValues } from '../PropertyFormDualPane/default-values';
import { transformToRequest } from '../PropertyFormDualPane/utils';
import {
  isTransactionPriceField,
  withPricedTransactions,
} from '../PropertyFormDualPane/priced-transactions';
import { tabsWithErrors } from './fieldTabMap';
import { useAgencyRequiredFields } from './agency-required-context';
import { blockedSaveMessage, revealFirstError } from './validation-summary';
import type { TabId } from './TabNavigation';
import { usePropertyDraft } from './usePropertyDraft';
import { saveErrorKind } from '@/shared/utils/save-error-kind';

export interface UsePropertyDrawerFormProps {
  agencyId: string;
  editingProperty?: Record<string, any> | null;
  onSuccess?: () => void;
  afterSave?: (propertyId: string) => Promise<void> | void;
  /** Whether the drawer is open; drafts are only read/written while it is. */
  open?: boolean;
}

/**
 * Returns the value when it is non-nullish, otherwise the fallback.
 * @returns The value or the fallback.
 */
function orDefault<T>(value: T | null | undefined, fallback: T): T {
  return value ?? fallback;
}

/**
 * Persists the property (update when editing, insert otherwise) and returns its id.
 * @returns The persisted property id, when available.
 */
async function persistProperty(
  agencyId: string,
  editingProperty: Record<string, any> | null | undefined,
  requestData: unknown,
): Promise<string | undefined> {
  if (editingProperty?.id) {
    await backendPropertiesCrud(agencyId).update({ id: editingProperty.id, body: requestData as any });
    return editingProperty.id;
  }
  const result = (await backendPropertiesCrud(agencyId).insert({ body: requestData as any })) as any;
  return result?.message || result?.detail || result?.id || result?.data?.id;
}

interface SaveContext {
  agencyId: string;
  editingProperty: Record<string, any> | null | undefined;
  reorderedImages: string[] | null;
  pendingImages: File[];
  /** The values the form opened with; lets an update clear emptied fields. */
  initialValues: PropertyFormValues;
  /** Called as soon as the server accepted the property (2xx), before uploads. */
  onPersisted?: () => void;
}

/**
 * Transforms the form data, persists the property, uploads pending images, and returns its id.
 * @returns The persisted property id, when available.
 */
async function saveProperty(data: PropertyFormValues, ctx: SaveContext): Promise<string | undefined> {
  const initial = ctx.editingProperty?.id ? ctx.initialValues : null;
  const requestData = transformToRequest(data, { initial }) as Record<string, unknown>;
  if (ctx.reorderedImages) requestData.images = ctx.reorderedImages;
  const propertyId = await persistProperty(ctx.agencyId, ctx.editingProperty, requestData);
  ctx.onPersisted?.();
  if (ctx.pendingImages.length > 0 && propertyId) {
    await uploadPropertyFileImages(ctx.pendingImages, propertyId, ctx.agencyId);
  }
  return propertyId;
}

/**
 * Fetches address data for a CEP and applies it to the form.
 * @returns A promise that resolves once the lookup completes.
 */
async function applyCepLookup(cep: string, form: UseFormReturn<PropertyFormValues>): Promise<void> {
  const digits = cep.replace(/\D/g, '');
  if (digits.length !== 8) return;
  const result = await getCepData(digits);
  if (!result.data) return;
  form.setValue('street', orDefault(result.data.logradouro, ''));
  form.setValue('neighborhood', orDefault(result.data.bairro, ''));
  form.setValue('city', orDefault(result.data.localidade, ''));
  form.setValue('state', orDefault(result.data.uf, ''));
}

/**
 * Keeps the transaction chips in step with the prices (plan §2): typing a
 * Temporada or Leilão price selects that transaction at once. Only a price
 * change triggers it, so the user can still turn a chip off afterwards.
 *
 * @param form - The property drawer form.
 */
export function useMarkPricedTransactions(form: UseFormReturn<PropertyFormValues>): void {
  useEffect(() => {
    const subscription = form.watch((values, { name }) => {
      if (!isTransactionPriceField(name)) return;
      const current = (values.transaction_type ?? []).filter((v): v is string => typeof v === 'string');
      const next = withPricedTransactions(current, values);
      if (next.length !== current.length) {
        form.setValue('transaction_type', next, { shouldDirty: true });
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);
}

/**
 * The toast/report message for a failed save: an anti-bot rejection is not a
 * data problem, a plain 400 is a validation problem, anything else is generic.
 *
 * @param error - The thrown error.
 * @param t - The feature translator.
 * @param draftKept - Whether the typed data was really stored locally.
 * @returns The user-facing message.
 */
function saveErrorMessage(error: any, t: (key: string) => string, draftKept: boolean): string {
  const kind = saveErrorKind(error);
  if (kind === 'antibot') return t(draftKept ? 'form.drawer.saveAntiBotKept' : 'form.drawer.saveAntiBot');
  return kind === 'validation' ? t('form.drawer.saveValidationError') : t('form.drawer.saveError');
}

type Translate = TFunction;

/**
 * Pending (picked but unsaved) photos. Videos and documents upload at once and
 * keep no pending state. `mediaKey` is bumped whenever the pending media is
 * dropped: the photo section keeps its own previews in state, so it is
 * remounted (keyed) to forget them too. Closing the drawer also drops them.
 *
 * @param open - Whether the drawer is open.
 * @returns The refs, the remount key and the reset action.
 */
function usePendingMedia(open: boolean) {
  const pendingImagesRef = useRef<File[]>([]);
  const reorderedImagesRef = useRef<string[] | null>(null);
  const [mediaKey, setMediaKey] = useState(0);

  const resetMedia = useCallback(() => {
    if (pendingImagesRef.current.length === 0 && reorderedImagesRef.current === null) return;
    pendingImagesRef.current = [];
    reorderedImagesRef.current = null;
    setMediaKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!open) resetMedia();
  }, [open, resetMedia]);

  return { pendingImagesRef, reorderedImagesRef, mediaKey, resetMedia };
}

/**
 * The agency's own required fields (Configurações > Imóveis) on top of the
 * form's floor. Until they load, the floor alone applies.
 *
 * @param agencyId - The agency whose settings apply.
 * @param t - The feature translator.
 * @returns The zod schema for the form and the agency settings it came from.
 */
function useDrawerSchema(agencyId: string, t: Translate) {
  const { settings } = usePropertySettings(agencyId);
  const requiredMessage = t('v2.imoveis.required.formMessage', { ns: 'features/agency-settings' });
  const schema = useMemo(
    () => (settings
      ? createPropertyFormSchema(undefined, { required: settings.required_fields, message: requiredMessage })
      : propertyFormSchema),
    [settings, requiredMessage],
  );
  return { schema, settings };
}

interface SubmitDeps {
  agencyId: string;
  editingProperty: Record<string, any> | null | undefined;
  defaultValues: PropertyFormValues;
  form: UseFormReturn<PropertyFormValues>;
  t: Translate;
  language: string;
  afterSave?: (propertyId: string) => Promise<void> | void;
  onSuccess?: () => void;
  clearDraft: () => void;
  keepDraft: () => boolean;
  resetMedia: () => void;
  pendingImagesRef: { current: File[] };
  reorderedImagesRef: { current: string[] | null };
}

/**
 * What follows a 2xx: a created form starts empty again, pending photos go,
 * and the user is told.
 *
 * @param d - The submit dependencies.
 */
function afterPersisted(d: SubmitDeps): void {
  if (!d.editingProperty) d.form.reset(d.defaultValues);
  d.resetMedia();
  toast.success(d.t(d.editingProperty ? 'form.drawer.updateSuccess' : 'form.drawer.saveSuccess'));
}

/**
 * Runs the whole save sequence for valid form data.
 *
 * @param data - The validated form values.
 * @param d - The submit dependencies.
 */
async function submitProperty(data: PropertyFormValues, d: SubmitDeps): Promise<void> {
  const propertyId = await saveProperty(data, {
    agencyId: d.agencyId,
    editingProperty: d.editingProperty,
    reorderedImages: d.reorderedImagesRef.current,
    pendingImages: d.pendingImagesRef.current,
    initialValues: d.defaultValues,
    onPersisted: d.clearDraft,
  });
  afterPersisted(d);
  if (propertyId) await d.afterSave?.(propertyId);
  d.onSuccess?.();
}

/**
 * Submit handlers and the validation/submitting state of the drawer.
 *
 * @param form - The property form.
 * @param deps - The submit dependencies.
 * @returns State plus `handleSubmit` and `clearErrors`.
 */
function useSubmitHandlers(form: UseFormReturn<PropertyFormValues>, deps: SubmitDeps) {
  const { t, language } = deps;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [erroredTabs, setErroredTabs] = useState<TabId[]>([]);
  const [validationMessage, setValidationMessage] = useState('');

  const clearErrors = useCallback(() => {
    setErroredTabs([]);
    setValidationMessage('');
  }, []);

  const onValid = useCallback(async (data: PropertyFormValues) => {
    setIsSubmitting(true);
    clearErrors();
    try {
      await submitProperty(data, deps);
    } catch (error: any) {
      reportError(error, { message: saveErrorMessage(error, t, deps.keepDraft()), autoClose: 6000 });
    } finally {
      setIsSubmitting(false);
    }
  }, [deps, t, clearErrors]);

  const onInvalid = useCallback((errors: Record<string, unknown>) => {
    const fields = Object.keys(errors);
    const message = blockedSaveMessage(fields, t, language);
    setErroredTabs(tabsWithErrors(errors as never));
    setValidationMessage(message);
    reportError(undefined, { message });
    requestAnimationFrame(() => revealFirstError(fields));
  }, [t, language]);

  const handleSubmit = useCallback(
    () => form.handleSubmit(onValid, onInvalid)(),
    [form, onValid, onInvalid],
  );

  return { isSubmitting, erroredTabs, validationMessage, handleSubmit, clearErrors };
}

/**
 * Manages the property drawer form: default values, submit sequence, CEP lookup,
 * per-tab validation state, pending image tracking and the local draft.
 * @param props - Agency id, the property under edit, and success/after-save callbacks.
 * @returns The form instance plus submit handlers, submitting flag, and errored-tab state.
 */
export function usePropertyDrawerForm(props: UsePropertyDrawerFormProps) {
  const { agencyId, editingProperty, onSuccess, afterSave, open = true } = props;
  const { t, i18n } = useTranslation('features/properties');
  const media = usePendingMedia(open);

  // Keyed on the property object, not its id: after a save the detail page
  // refetches the same property (same id, new data), and an id-only key kept
  // the form seeded with the pre-edit values until a full page reload.
  const defaultValues = useMemo(
    () => buildDefaultValues(editingProperty),
    [editingProperty, agencyId],
  );
  const { schema, settings } = useDrawerSchema(agencyId, t);
  const form = useForm<PropertyFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onSubmit',
  });

  const requiredFields = useAgencyRequiredFields(form.control, settings?.required_fields);
  useMarkPricedTransactions(form);
  const draft = usePropertyDraft({ form, agencyId, editingProperty, defaultValues, open });
  const { clear: clearDraft, keep: keepDraft } = draft;
  const { resetMedia, pendingImagesRef, reorderedImagesRef } = media;

  const deps = useMemo<SubmitDeps>(() => ({
    agencyId, editingProperty, defaultValues, form, t, language: i18n.language, afterSave, onSuccess,
    clearDraft, keepDraft, resetMedia, pendingImagesRef, reorderedImagesRef,
  }), [agencyId, editingProperty, defaultValues, form, t, i18n.language, afterSave, onSuccess,
    clearDraft, keepDraft, resetMedia, pendingImagesRef, reorderedImagesRef]);
  const submit = useSubmitHandlers(form, deps);
  const { clearErrors } = submit;

  useEffect(() => {
    form.reset(defaultValues);
    resetMedia();
    clearErrors();
  }, [defaultValues, form, resetMedia, clearErrors]);

  const lookupCep = useCallback((cep: string) => applyCepLookup(cep, form), [form]);

  return {
    form,
    isSubmitting: submit.isSubmitting,
    erroredTabs: submit.erroredTabs,
    firstErroredTab: submit.erroredTabs[0],
    validationMessage: submit.validationMessage,
    handleSubmit: submit.handleSubmit,
    lookupCep,
    pendingImagesRef,
    reorderedImagesRef,
    isEditMode: !!editingProperty,
    mediaKey: media.mediaKey,
    draftOffer: draft.offer,
    restoreDraft: draft.restore,
    discardDraft: draft.discard,
    requiredFields,
  };
}
